import { apiClient } from '@api'

/**
 * Company Service
 *
 * Service layer for managing companies
 * Provides methods for CRUD operations on companies
 */
class CompanyService {
	/**
	 * Get all companies
	 * @param {Object} params - Query parameters (page, limit, status, search, package_id)
	 * @returns {Promise<Object>} Companies data with pagination meta
	 */
	async getAll(params = {}) {
		const queryParams = new URLSearchParams()

		if (params.page) queryParams.append('page', params.page)
		if (params.limit) queryParams.append('limit', params.limit)
		if (params.status) queryParams.append('status', params.status)
		if (params.search) queryParams.append('search', params.search)
		if (params.package_id) queryParams.append('package_id', params.package_id)

		const queryString = queryParams.toString()
		const endpoint = queryString ? `/companies?${queryString}` : '/companies'

		return await apiClient.get(endpoint)
	}

	/**
	 * Get company by ID
	 * @param {string|number} id - Company ID
	 * @returns {Promise<Object>} Company data
	 */
	async getById(id) {
		return await apiClient.get(`/companies/${id}`)
	}

	/**
	 * Get company by slug
	 * @param {string} slug - Company slug
	 * @returns {Promise<Object>} Company data
	 */
	async getBySlug(slug) {
		return await apiClient.get(`/companies/slug/${slug}`)
	}

	/**
	 * Create new company
	 * @param {Object} companyData - Company data
	 * @returns {Promise<Object>} Created company data
	 */
	async create(companyData) {
		return await apiClient.post('/companies', companyData)
	}

	/**
	 * Update company
	 * @param {string|number} id - Company ID
	 * @param {Object} companyData - Updated company data
	 * @returns {Promise<Object>} Updated company data
	 */
	async update(id, companyData) {
		return await apiClient.put(`/companies/${id}`, companyData)
	}

	/**
	 * Delete company (archive - soft delete)
	 * @param {string|number} id - Company ID
	 * @returns {Promise<Object>} Success response
	 */
	async delete(id) {
		return await apiClient.delete(`/companies/${id}`)
	}

	/**
	 * Restore archived company
	 * @param {string|number} id - Company ID
	 * @returns {Promise<Object>} Restored company data
	 */
	async restore(id) {
		return await apiClient.post(`/companies/${id}/restore`, {})
	}

	/**
	 * Activate company account
	 * @param {string|number} id - Company ID
	 * @returns {Promise<Object>} Updated company data
	 */
	async activate(id) {
		return await apiClient.post(`/companies/${id}/activate`, {})
	}

	/**
	 * Suspend company account
	 * @param {string|number} id - Company ID
	 * @returns {Promise<Object>} Updated company data
	 */
	async suspend(id) {
		return await apiClient.post(`/companies/${id}/suspend`, {})
	}

	/**
	 * Cancel company subscription
	 * @param {string|number} id - Company ID
	 * @returns {Promise<Object>} Updated company data
	 */
	async cancel(id) {
		return await apiClient.post(`/companies/${id}/cancel`, {})
	}

	/**
	 * Update company package (upgrade/downgrade)
	 * @param {string|number} id - Company ID
	 * @param {string|number} packageId - New package ID
	 * @returns {Promise<Object>} Updated company data
	 */
	async updatePackage(id, packageId) {
		return await apiClient.put(`/companies/${id}/package`, { package_id: packageId })
	}

	/**
	 * Update compliance manager
	 * @param {string|number} id - Company ID
	 * @param {string|number} userId - User ID
	 * @returns {Promise<Object>} Updated company data
	 */
	async updateManager(id, userId) {
		return await apiClient.put(`/companies/${id}/manager`, { user_id: userId })
	}

	/**
	 * Get all packages (for dropdown filters)
	 * @returns {Promise<Object>} Packages data
	 */
	async getPackages() {
		return await apiClient.get('/packages')
	}
}

// Export singleton instance
export const companyService = new CompanyService()
export default companyService
